import SpeakButton from "@/components/SpeakButton";
import { useState } from "react";
import { Flag, Star, Trash2, X } from "lucide-react";
import { useT } from "@/i18n";
import { BLOCK_TYPES, GAME_TEXT } from "@/components/games/content";

const STAR_X = 80;
const wait = (ms) => new Promise((r) => setTimeout(r, ms));
const START = { x: 10, y: 0, rot: 0, say: "", got: false };

export default function BlockBuilder() {
  const { lang } = useT();
  const tx = GAME_TEXT[lang];
  const [script, setScript] = useState([]);
  const [lumi, setLumi] = useState(START);
  const [running, setRunning] = useState(false);

  const run = async () => {
    setRunning(true);
    let s = { ...START };
    setLumi(s);
    await wait(300);
    for (const b of script) {
      if (b === "right") s = { ...s, x: Math.min(85, s.x + 15) };
      if (b === "left") s = { ...s, x: Math.max(0, s.x - 15) };
      if (b === "spin") s = { ...s, rot: s.rot + 360 };
      if (b === "say") s = { ...s, say: lang === "es" ? "¡Hola!" : "Hello!" };
      if (b === "grab" && Math.abs(s.x - STAR_X) <= 10) s = { ...s, got: true, say: tx.got };
      if (b === "jump") { setLumi({ ...s, y: 40 }); await wait(300); }
      setLumi(s);
      await wait(550);
    }
    setRunning(false);
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2"><h3 className="font-fredoka text-xl sm:text-2xl font-bold text-blue-950">{tx.builder}</h3><SpeakButton compact mascot="lumi" text={`${tx.builder} ${tx.builderSub}`} testId="ask-games-builder" /></div>
      <p className="text-blue-900 font-semibold">{tx.builderSub}</p>
      <div className="mt-4 grid lg:grid-cols-2 gap-5">
        <div className="bg-white rounded-3xl p-4 shadow-md min-h-[16rem]" data-testid="block-script">
          <div className="bg-yellow-400 rounded-xl px-4 py-2 font-bold text-blue-950 inline-flex items-center gap-2"><Flag className="w-4 h-4" /> {tx.whenFlag}</div>
          {script.length === 0 && <p className="mt-4 text-sm font-semibold text-blue-900/60">{tx.empty}</p>}
          <div className="mt-1 space-y-1">
            {script.map((id, i) => {
              const b = BLOCK_TYPES.find((t) => t.id === id);
              return (
                <div key={i} className={`${b.color} ml-4 rounded-xl px-4 py-2 font-bold text-white text-sm flex justify-between items-center`} data-testid={`script-block-${i}`}>
                  {b[lang]}
                  <button type="button" onClick={() => setScript((sc) => sc.filter((_, j) => j !== i))} disabled={running} aria-label="remove"><X className="w-4 h-4" /></button>
                </div>
              );
            })}
          </div>
        </div>
        <div className="relative bg-gradient-to-b from-sky-200 to-lime-200 rounded-3xl border-4 border-white shadow-lg h-64 overflow-hidden" data-testid="block-stage">
          {!lumi.got && <Star className="absolute bottom-6 w-10 h-10 text-yellow-400 fill-yellow-300 sparkle" style={{ left: `${STAR_X + 4}%` }} />}
          <div className="absolute bottom-4 w-20 transition-all duration-500 ease-out" style={{ left: `${lumi.x}%`, transform: `translateY(-${lumi.y}px) rotate(${lumi.rot}deg)` }}>
            {lumi.say && <span className="absolute -top-10 left-1/2 -translate-x-1/2 whitespace-nowrap bg-white rounded-2xl px-3 py-1 text-sm font-bold text-blue-950 shadow" data-testid="block-say">{lumi.say}</span>}
            <img src="/img/lumi.webp" alt="Lumi" className="w-full" />
          </div>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        {BLOCK_TYPES.map((b) => (
          <button key={b.id} type="button" disabled={running} onClick={() => setScript((s) => [...s, b.id].slice(0, 12))} className={`${b.color} rounded-xl px-4 py-2 font-bold text-white text-sm shadow hover:-translate-y-0.5 transition-transform disabled:opacity-50`} data-testid={`add-block-${b.id}`}>
            + {b[lang]}
          </button>
        ))}
      </div>
      <div className="mt-4 flex gap-3">
        <button type="button" onClick={run} disabled={running || !script.length} className="glossy-btn bg-lime-400 text-blue-950 font-fredoka font-bold text-lg px-6 py-2 rounded-full inline-flex items-center gap-2 disabled:opacity-60" data-testid="block-run-btn">
          <Flag className="w-5 h-5" /> {tx.run}
        </button>
        <button type="button" onClick={() => { setScript([]); setLumi(START); }} disabled={running} className="bg-white rounded-full px-5 py-2 font-bold text-blue-900 inline-flex items-center gap-2 shadow" data-testid="block-clear-btn">
          <Trash2 className="w-4 h-4" /> {tx.clear}
        </button>
      </div>
    </div>
  );
}
